// Styles
import '@design/normalize.css'

// Vue core
import Vue from 'vue'

// App core
import App from './app'
import router from '@router'
import store from '@state/store'
import '@components/_globals'
import './registerServiceWorker'

// Services
import messageService from '@services/message-service'
import analyticsService from '@services/analytics-service'
import consentService from '@services/consent-service'

// Analytics
import mixpanelService from '@services/mixpanel-service'
import superPropertiesManager from '@services/mixpanel/super-properties-manager'
import appLoadTracker from '@services/mixpanel/app-load-tracker'
import playbackTracker from '@services/mixpanel/playback-tracker'

// Directives
import clickOutside from '@directives/clickOutside'
import safeHtml from '@directives/safeHtml'

// Global Configuration
Vue.config.productionTip = process.env.NODE_ENV === 'production'

// Vue Prototype Services
Vue.prototype.$queueService = store.state.services.queueService
Vue.prototype.$playerService = store.state.services.playerService
Vue.prototype.$keyControlsService = store.state.services.keyControlsService
Vue.prototype.$m = messageService
Vue.prototype.$analytics = analyticsService

// Player Service Setup
Vue.prototype.$playerService.setVolume(store.state.settings.volume)
Vue.prototype.$playerService.on('settingsUpdate', val => store.commit('settings/SET_SETTINGS', val))

// Mixpanel Analytics
const mixpanelToken = process.env.VUE_APP_MIXPANEL_TOKEN
if (mixpanelToken) {
    mixpanelService.init(mixpanelToken, {
        debug: process.env.NODE_ENV !== 'production',
        persistence: 'localStorage',
        track_pageview: false,
        batch_requests: true,
        opt_out_tracking_by_default: true,
    })

    consentService.restoreConsentState()

    // Auto-grant consent for logged-in users without consent (grandfathered)
    if (!consentService.hasConsent()) {
        const currentUser = store.state.auth.currentUser
        if (currentUser) {
            consentService.grantConsent('grandfathered')
        }
    }

    superPropertiesManager.initialize()

    const currentUser = store.state.auth.currentUser
    if (currentUser) {
        const memberId = currentUser.members_id?.id ? String(currentUser.members_id.id) : null
        if (memberId) {
            superPropertiesManager.updateUserStatus(true, memberId)
            mixpanelService.identify(memberId)
        }
    }

    appLoadTracker.start()

    // Report any listening from a session that crashed without a clean end (once, at boot).
    playbackTracker.recoverJournaledSession()

    document.addEventListener('visibilitychange', () => playbackTracker.onVisibilityChange(document.hidden))
    window.addEventListener('pagehide', () => playbackTracker.onPageHide())
    // Page Lifecycle: a tab about to be discarded/frozen — beacon-flush but keep the session
    // so a later `resume` continues counting; if discarded, boot recovery picks up the rest.
    document.addEventListener('freeze', () => playbackTracker.onVisibilityChange(true))
}
Vue.prototype.$mixpanel = mixpanelService

// Vue Directives
Vue.directive('click-outside', clickOutside)
Vue.directive('safe-html', safeHtml)

// App Initialization
const app = new Vue({
    router,
    store,
    render: h => h(App),
}).$mount('#app')

// Cypress Testing Support
if (window.Cypress) {
    window.__app__ = app
}
